import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { ActionButton, HStack, UserAvatar } from "@zfp-akademie-a-s/zfp-design";
import { CheckIcon, DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { AgentSummary } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getAgentFullName } from "./utils";

type Props = {
  readonly advisor: AgentSummary;
  readonly isShared: boolean;
  readonly shareDisabledReason: string | undefined;
  readonly disabled: boolean;
  readonly onShare: () => void;
  readonly onRemove: () => void;
};

export const SharingAdvisorItem = ({ advisor, isShared, shareDisabledReason, disabled, onShare, onRemove }: Props) => {
  const { t } = useResource();
  const fullName = getAgentFullName(advisor);
  const removeLabel = t(Resources.AnalysisDetail.Sharing.Remove);
  const isRemoveDisabled = isShared || disabled;

  return (
    <HStack alignItems="center" justifyContent="space-between" spacing={Spacing.L} py={Spacing.L} px={Spacing.XL}>
      <HStack alignItems="center" spacing={Spacing.L} minWidth={0}>
        <UserAvatar firstName={advisor.firstName} lastName={advisor.lastName} />
        <Box minWidth={0}>
          <Typography variant="body1" fontWeight={500} noWrap>
            {fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.AdvisorId, { advisorId: advisor.ssoId })}
          </Typography>
        </Box>
      </HStack>
      <HStack alignItems="center" spacing={Spacing.M} flexShrink={0}>
        {isShared ? (
          <Chip color="success" icon={<CheckIcon />} label={t(Resources.AnalysisDetail.Sharing.Shared)} />
        ) : (
          <Tooltip title={shareDisabledReason ?? ""}>
            <Box component="span" display="inline-flex">
              <Button variant="contained" color="secondary" disabled={!!shareDisabledReason || disabled} onClick={onShare}>
                {t(Resources.AnalysisDetail.Sharing.Share)}
              </Button>
            </Box>
          </Tooltip>
        )}
        <ActionButton label={removeLabel} icon={<DeleteIcon />} tone="danger" disabled={isRemoveDisabled} onClick={onRemove} />
      </HStack>
    </HStack>
  );
};
